import { formatPostingDate, toTrimmedString } from './shared';

const toBooleanFlag = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return null;
    if (['true', 'yes', 'y', '1', 'selected', 'active'].includes(normalized)) return true;
    if (['false', 'no', 'n', '0', 'unselected', 'inactive'].includes(normalized)) return false;
  }
  return null;
};

const normalizeSublocationEntry = (sublocation) => {
  if (!sublocation) return null;

  if (typeof sublocation === 'string') {
    const name = toTrimmedString(sublocation);
    if (!name) return null;
    return {
      id: '',
      name,
      endDate: '',
      selectedFlag: null,
    };
  }

  if (typeof sublocation !== 'object') return null;

  const id = String(sublocation?.id ?? sublocation?.sublocationId ?? sublocation?.value ?? '').trim();
  const name = toTrimmedString(sublocation?.name ?? sublocation?.sublocationName ?? sublocation?.label);
  if (!name) return null;

  const endDateRaw =
    sublocation?.endDate ??
    sublocation?.lastPosting?.endDate ??
    sublocation?.lastPostingEndDate ??
    sublocation?.inEffectTo ??
    null;
  const endDate = formatPostingDate(endDateRaw) || '';
  const selectedFlag = toBooleanFlag(sublocation?.isSelected ?? sublocation?.selected ?? sublocation?.is_selected);

  return {
    id,
    name,
    endDate,
    selectedFlag,
  };
};

const normalizeSublocationList = (list) => {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.map((item) => normalizeSublocationEntry(item)).filter(Boolean);
};

const toSublocationKey = (entry) =>
  `${String(entry?.id ?? '').trim()}|${String(entry?.name ?? '')
    .trim()
    .toLowerCase()}`;

const dedupeSublocationEntries = (entries) => {
  const byKey = new Map();
  entries.forEach((entry) => {
    const key = toSublocationKey(entry);
    if (!key || byKey.has(key)) return;
    byKey.set(key, entry);
  });
  return Array.from(byKey.values());
};

const removeSelectedFromUnselected = (selectedEntries, unselectedEntries) => {
  if (!unselectedEntries.length) return [];
  const selectedKeys = new Set(selectedEntries.map((entry) => toSublocationKey(entry)));
  return unselectedEntries.filter((entry) => {
    const key = toSublocationKey(entry);
    return !selectedKeys.has(key);
  });
};

const normalizeSublocationIdList = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];

  const normalizedIds = items
    .map((item) => {
      if (item === null || item === undefined) return '';
      if (typeof item === 'object') {
        return String(item?.id ?? item?.sublocationId ?? item?.value ?? '').trim();
      }
      return String(item).trim();
    })
    .filter(Boolean);

  return Array.from(new Set(normalizedIds));
};

const resolveSelectedSublocationIds = (posting = {}) => {
  const possibleIdLists = [
    posting?.sublocationIds,
    posting?.subLocationIds,
    posting?.sublocationsIds,
    posting?.selectedSublocationIds,
    posting?.selectedSubLocationIds,
    posting?.selected_sublocation_ids,
    posting?.selected_sub_location_ids,
  ];

  for (const list of possibleIdLists) {
    const normalized = normalizeSublocationIdList(list);
    if (normalized.length > 0) return normalized;
  }

  return [];
};

export const extractPostingSublocationBuckets = (posting = {}) => {
  if (!posting || typeof posting !== 'object') {
    return {
      selectedSublocations: [],
      unselectedSublocations: [],
    };
  }

  const selectedFromExplicit = normalizeSublocationList(
    posting?.selectedSublocations ?? posting?.selected_sublocations ?? posting?.selectedSubLocations,
  );
  const unselectedFromExplicit = normalizeSublocationList(
    posting?.unselectedSublocations ?? posting?.unselected_sublocations ?? posting?.unselectedSubLocations,
  );

  const genericSublocations = normalizeSublocationList(posting?.sublocations ?? posting?.subLocations);
  const hasExplicitSelectionFlags = genericSublocations.some((entry) => entry?.selectedFlag !== null);
  const hasExplicitBuckets = selectedFromExplicit.length > 0 || unselectedFromExplicit.length > 0;
  const selectedIdSet = new Set(resolveSelectedSublocationIds(posting));
  const hasSelectedIds = selectedIdSet.size > 0;
  const explicitSelectedKeySet = new Set(selectedFromExplicit.map((entry) => toSublocationKey(entry)));
  const explicitUnselectedKeySet = new Set(unselectedFromExplicit.map((entry) => toSublocationKey(entry)));

  let selectedFromGeneric = [];
  let unselectedFromGeneric = [];

  if (hasExplicitSelectionFlags) {
    selectedFromGeneric = genericSublocations.filter((entry) => entry?.selectedFlag !== false);
    unselectedFromGeneric = genericSublocations.filter((entry) => entry?.selectedFlag === false);
  } else if (hasSelectedIds && genericSublocations.length > 0) {
    selectedFromGeneric = genericSublocations.filter((entry) => {
      const id = String(entry?.id ?? '').trim();
      return id ? selectedIdSet.has(id) : false;
    });
    unselectedFromGeneric = genericSublocations.filter((entry) => {
      const id = String(entry?.id ?? '').trim();
      return id ? !selectedIdSet.has(id) : false;
    });
  } else if (hasExplicitBuckets && genericSublocations.length > 0) {
    if (selectedFromExplicit.length > 0 && unselectedFromExplicit.length === 0) {
      unselectedFromGeneric = genericSublocations.filter(
        (entry) => !explicitSelectedKeySet.has(toSublocationKey(entry)),
      );
    }
    if (unselectedFromExplicit.length > 0 && selectedFromExplicit.length === 0) {
      selectedFromGeneric = genericSublocations.filter(
        (entry) => !explicitUnselectedKeySet.has(toSublocationKey(entry)),
      );
    }
  } else {
    selectedFromGeneric = genericSublocations;
  }

  const selectedSublocations = dedupeSublocationEntries([...selectedFromExplicit, ...selectedFromGeneric]);
  const unselectedSublocations = removeSelectedFromUnselected(
    selectedSublocations,
    dedupeSublocationEntries([...unselectedFromExplicit, ...unselectedFromGeneric]),
  );

  return {
    selectedSublocations,
    unselectedSublocations,
  };
};
